import ChevronLeftIcon from '@ordr-as/icons/ChevronLeftIcon';
import { Dialog } from '@ordr-as/umami-react/dialog';
import { TextInput } from '@ordr-as/umami-react/form';
import { FC, useCallback, useMemo, useState } from 'react';
import { CartOrderLine, CartOrderLineComponent } from '../../../../../../../types/cart';
import { useScreenSize } from '../../../../../../context/ScreenSizeContext';
import { useCartStore } from '../../../../../../context/cart/CartStore';
import useIntl from '../../../../../../hooks/useIntl';
import { getUniqueOrderLineKey } from '../../../../../../utils/orderLineUtils';
import ComponentListRow from './composite/ComponentListRow';
import { ExpandedComponent, collapseComponents, expandComponents, toBaseComponent } from './composite/types';
import { useCompositeNavigation } from './composite/useCompositeNavigation';

interface Props {
  onModalClose: VoidFunction;
  parentOrderLine: CartOrderLine;
}

/** Recursively collect all expanded component instanceIds with their current comments */
const collectComponentComments = (components: ExpandedComponent[]): Map<string, string> => {
  const result = new Map<string, string>();
  for (const component of components) {
    result.set(component.instanceId, component.comment || '');
    if (component.components && component.components.length > 0) {
      for (const [id, comment] of collectComponentComments(component.components)) {
        result.set(id, comment);
      }
    }
  }
  return result;
};

/** Recursively build save-ready components from expanded state with updated comments */
const buildSaveComponents = (
  expanded: ExpandedComponent[],
  commentsMap: Map<string, string>
): CartOrderLineComponent[] =>
  expanded.map((ec) => {
    const comment = commentsMap.get(ec.instanceId) || undefined;
    const children = ec.components as ExpandedComponent[] | undefined;
    return toBaseComponent(ec, {
      comment,
      commentId: comment ? ec.commentId || crypto.randomUUID() : undefined,
      components: children ? buildSaveComponents(children, commentsMap) : undefined,
    });
  });

const CompositeCommentModal: FC<Props> = ({ parentOrderLine, onModalClose }) => {
  const { isPhone } = useScreenSize();
  const intl = useIntl();
  const updateCompositeProduct = useCartStore((x) => x.updateCompositeProduct);

  const expandedRootComponents = useMemo(
    () => expandComponents(parentOrderLine.components ?? []),
    [parentOrderLine.components]
  );
  const { push, pop, currentComponent, currentTitle, viewMode, listComponents, canGoBack } =
    useCompositeNavigation(expandedRootComponents);

  const [parentComment, setParentComment] = useState(parentOrderLine.comment || '');
  const [componentCommentsMap, setComponentCommentsMap] = useState<Map<string, string>>(() =>
    collectComponentComments(expandedRootComponents)
  );

  const updateComponentComment = useCallback((instanceId: string, comment: string) => {
    setComponentCommentsMap((prev) => {
      const updated = new Map(prev);
      updated.set(instanceId, comment);
      return updated;
    });
  }, []);

  const handleSave = useCallback(() => {
    updateCompositeProduct({
      orderLineKey: getUniqueOrderLineKey(parentOrderLine),
      parentOptions: parentOrderLine.options,
      parentComment,
      quantity: parentOrderLine.quantity,
      componentUpdates: [],
      components: collapseComponents(buildSaveComponents(expandedRootComponents, componentCommentsMap)),
    });
    onModalClose();
  }, [
    updateCompositeProduct,
    parentOrderLine,
    parentComment,
    expandedRootComponents,
    componentCommentsMap,
    onModalClose,
  ]);

  const dialogTitle = currentTitle ?? intl.formatMessage({ id: 'checkout.note.header' });

  return (
    <Dialog
      onClose={onModalClose}
      title={dialogTitle}
      actionLabel={intl.formatMessage({ id: 'generic.save' })}
      onAction={handleSave}
      size={isPhone ? 'fullwidth' : 'default'}
      TitleIcon={canGoBack ? ChevronLeftIcon : undefined}
      titleIconAction={canGoBack ? pop : undefined}
      titleIconClassNames={canGoBack ? '!mr-1 !p-0' : undefined}
      alignTop
    >
      <article className="flex flex-col gap-3">
        {viewMode === 'root' && (
          <TextInput
            value={parentComment}
            onChange={setParentComment}
            label={intl.formatMessage({ id: 'checkout.note.header' })}
            multiline
            maxLength={140}
            fullWidth
            autoFocus
          />
        )}

        {viewMode === 'component-list' && currentComponent && (
          <TextInput
            value={componentCommentsMap.get(currentComponent.instanceId) || ''}
            onChange={(value) => updateComponentComment(currentComponent.instanceId, value)}
            label={intl.formatMessage({ id: 'checkout.note.header' })}
            multiline
            maxLength={140}
            fullWidth
            autoFocus
          />
        )}

        {viewMode === 'component-detail' && currentComponent && (
          <TextInput
            value={componentCommentsMap.get(currentComponent.instanceId) || ''}
            onChange={(value) => updateComponentComment(currentComponent.instanceId, value)}
            label={intl.formatMessage({ id: 'checkout.note.header' })}
            multiline
            maxLength={140}
            fullWidth
            autoFocus
          />
        )}

        {(viewMode === 'root' || viewMode === 'component-list') &&
          listComponents.map((component) => (
            <ComponentListRow
              key={component.instanceId}
              component={component}
              subtitle={componentCommentsMap.get(component.instanceId) || undefined}
              isValid={true}
              onClick={() =>
                push({
                  instanceId: component.instanceId,
                  itemVariantId: component.itemVariantId,
                  name: component.name,
                })
              }
            />
          ))}
      </article>
    </Dialog>
  );
};

export default CompositeCommentModal;
